import type { ApiErrorBody } from '@/types';

/**
 * Lightweight fetch wrapper for the Provider API.
 *
 * Responsibilities:
 *   - Prefix `<VITE_API_BASE_URL>/provider/v1` (origin is configurable).
 *   - Attach Bearer + JSON headers.
 *   - On 401: try a one-shot refresh; if that also fails, redirect to /login.
 *   - Surface backend errors as throwable `ApiError` so views can `try/catch`.
 *
 * Refresh logic is intentionally non-recursive: the refresh call itself
 * uses `rawFetch` to avoid an infinite loop if the refresh token is
 * also invalid.
 *
 * Base URL resolution:
 *   - If `VITE_API_BASE_URL` is set, all requests go to that origin
 *     (useful for pointing dev at a remote staging API).
 *   - Otherwise we use same-origin relative paths, which works both
 *     under the Vite dev proxy and behind an nginx reverse proxy in prod.
 */
const ENV_BASE_URL =
    (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') || '';
const BASE = `${ENV_BASE_URL}/provider/v1`;

/**
 * Origen de la API, para las URLs que abre el NAVEGADOR en vez de `fetch`
 * (hoy, el PDF del cuestionario). Cuando está vacío son rutas relativas al
 * mismo origen, que es como corre detrás del proxy de Vite y de nginx.
 */
export const apiOrigin = (): string => ENV_BASE_URL || window.location.origin;

const ACCESS_KEY = 'bt_provider_access_token';
const REFRESH_KEY = 'bt_provider_refresh_token';

export const tokenStorage = {
    getAccess: () => localStorage.getItem(ACCESS_KEY),
    getRefresh: () => localStorage.getItem(REFRESH_KEY),
    set(access: string, refresh: string) {
        localStorage.setItem(ACCESS_KEY, access);
        localStorage.setItem(REFRESH_KEY, refresh);
    },
    clear() {
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
    },
};

export class ApiError extends Error {
    constructor(public status: number, message: string, public body?: ApiErrorBody) {
        super(message);
        this.name = 'ApiError';
    }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOpts<TBody> {
    method?: Method;
    body?: TBody;
    auth?: boolean;
    /** When true, sends the body as FormData (for file uploads). */
    formData?: boolean;
    /** Skip the 401-refresh cycle (used by /auth/refresh itself). */
    skipRefresh?: boolean;
    /** 'blob' returns the raw response body (binary downloads, e.g. .xlsx export). */
    responseType?: 'json' | 'blob';
}

async function rawFetch<TResponse>(
    path: string,
    opts: RequestOpts<unknown>,
): Promise<TResponse> {
    const headers: Record<string, string> = {};
    if (!opts.formData) headers['Content-Type'] = 'application/json';
    if (opts.auth !== false) {
        const token = tokenStorage.getAccess();
        if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    let body: BodyInit | undefined;
    if (opts.body !== undefined) {
        body = opts.formData ? (opts.body as FormData) : JSON.stringify(opts.body);
    }

    const res = await fetch(`${BASE}${path}`, {
        method: opts.method ?? 'GET',
        headers,
        body,
    });

    if (res.status === 204) return undefined as TResponse;

    // Descarga binaria (xlsx export): devolvemos el Blob crudo. En error igual
    // intentamos leer el cuerpo como texto/JSON para surfacear el mensaje.
    if (opts.responseType === 'blob' && res.ok) {
        return (await res.blob()) as TResponse;
    }

    let parsed: unknown = null;
    const text = await res.text();
    if (text) {
        try {
            parsed = JSON.parse(text);
        } catch {
            parsed = text;
        }
    }

    if (!res.ok) {
        const errBody = (parsed as ApiErrorBody | null) || { message: res.statusText };

        /*
         * El código y la ruta van en el mensaje cuando el backend no mandó uno.
         *
         * Antes quedaba un "Request failed" pelado, que es lo que se ve cuando
         * la respuesta no trae cuerpo **ni** `statusText` (en HTTP/2 siempre
         * viene vacío). Ese mensaje no dice si fue un 401, un 403 o un 500, y
         * ya nos costó dos rondas de diagnóstico a ciegas.
         */
        const fallback = `Request failed (${res.status} on ${path})`;

        /*
         * 402 = la cuenta se pasó del cupo de su suscripción (§33).
         *
         * Se avisa por un evento del `window` en vez de llamar acá al composable
         * de la suscripción: ese composable consume este mismo módulo, y el
         * import cruzado dejaría un ciclo. El aviso de la cabecera escucha y se
         * refresca, así el cartel aparece en el acto en vez de en la próxima
         * recarga.
         */
        if (res.status === 402) window.dispatchEvent(new CustomEvent('bt:quota-exceeded'));

        throw new ApiError(res.status, errBody.message || fallback, errBody);
    }
    return parsed as TResponse;
}

/**
 * Attempts to refresh the access token using the stored refresh token.
 * Returns true if the refresh succeeded.
 */
async function refresh(): Promise<boolean> {
    const refreshToken = tokenStorage.getRefresh();
    if (!refreshToken) return false;
    try {
        const data = await rawFetch<{ accessToken: string; refreshToken: string }>(
            '/auth/refresh',
            { method: 'POST', body: { refreshToken }, auth: false, skipRefresh: true },
        );
        tokenStorage.set(data.accessToken, data.refreshToken);
        return true;
    } catch {
        tokenStorage.clear();
        return false;
    }
}

export async function apiRequest<TResponse, TBody = unknown>(
    path: string,
    opts: RequestOpts<TBody> = {},
): Promise<TResponse> {
    try {
        return await rawFetch<TResponse>(path, opts as RequestOpts<unknown>);
    } catch (e) {
        /*
         * `auth: false` queda fuera del rebote: son las pantallas públicas
         * (accept-invite, el formulario del QR) y ahí no hay sesión que perder.
         *
         * Sin esto, un 401 en una página pública mandaba al login del provider
         * a alguien que no es provider — que es lo que le pasaba al buzo que
         * escaneaba el QR desde el celular: el formulario abría y desaparecía.
         */
        if (e instanceof ApiError && e.status === 401 && !opts.skipRefresh && opts.auth !== false) {
            const ok = await refresh();
            if (ok) {
                return rawFetch<TResponse>(path, opts as RequestOpts<unknown>);
            }
            // Token chain is dead — wipe and bounce to login. Pages will
            // pick up the cleared state via the auth store hydration.
            tokenStorage.clear();
            if (!window.location.pathname.startsWith('/login')) {
                window.location.href = '/login';
            }
        }
        throw e;
    }
}
